import {
	type EntryPointRef,
	FLIGHT_DELAY_THRESHOLD_MINUTES,
	type FlightStatus,
	type FlightSummary,
	type Journey,
	type JourneyConnection,
	type TravelDirection,
	type TravelLeg,
	type TravelMode,
	formatFlightDesignator,
	legFlightChain,
} from "@seagames/contracts";
import { formatClockIn, viewerTimeZone, wallTimeIn } from "../../../lib/format/datetime";
import { intlLocaleFor } from "../../../lib/i18n";

/**
 * The flight card's model (A&D flight card, approved 2026-11-03) — pure, so
 * the four placements (journey list, journey detail, record page, leg form
 * lookup) read the same rules and the rules are unit-tested.
 *
 * TIME ZONES (CLAUDE.md, Travel › "Airport time zones" and Conventions ›
 * Time): on this card ONLY, each end of a flight is printed in its AIRPORT's
 * own zone (`FlightSummary.*_time_zone`, `JourneyConnection.*_time_zone`),
 * the zone written beside it. An airport the API knows no zone for falls back
 * to the viewer's own zone, labelled "your time". A TYPED time (no flight on
 * record: a coach, a ferry, a number no schedule has) was entered in the
 * Games' zone and is printed there. Everything else on the screens keeps its
 * own rule.
 *
 * Nothing here decides anything the API holds: the status, `at_risk`, the
 * counts are the API's; the card only lays them out. The two derived times
 * (`pickupAt`, `atAirportBy`) are display mirrors of the documented rules
 * (pickup = expected landing + the entry point's dwell; at the airport by =
 * expected take-off − its departure lead).
 */

/** How an end's time zone was decided: the airport's own, the Games' (a typed time), or the viewer's (airport zone unknown). */
export type CardZoneKind = "airport" | "typed" | "viewer";
export interface CardZone {
	timeZone: string;
	kind: CardZoneKind;
}

/** The airport's zone, else the viewer's ("your time"). */
export const airportZone = (tz: string | null | undefined): CardZone => (tz ? { timeZone: tz, kind: "airport" } : { timeZone: viewerTimeZone(), kind: "viewer" });
/** A typed time: the Games' zone it was entered in. */
export const typedZone = (gamesTimeZone: string): CardZone => ({ timeZone: gamesTimeZone, kind: "typed" });

export interface CardEnd {
	/** The IATA code (or an entry point's code); null when unknown. */
	code: string | null;
	/** A city / airport / entry point name, or the typed origin text. */
	place: string | null;
	terminal: string | null;
	/** The expected instant (actual ?? estimated ?? scheduled), ISO UTC; null = not known. */
	at: string | null;
	/** The scheduled instant, struck through when `at` moved from it. */
	scheduled: string | null;
	zone: CardZone;
}

export interface FlightCardModel {
	/** A flight row is on record (tracked or set by hand); false = a typed service: the degraded card. */
	tracked: boolean;
	direction: TravelDirection;
	mode: TravelMode;
	/** The carrier code for the logo; null when there is no flight. */
	carrier: string | null;
	/** "MH 803", the service as typed, or null (the mode's name is shown then). */
	name: string | null;
	/** The flight's day (provider's service date) or the journey's travel date — `YYYY-MM-DD`. */
	day: string | null;
	from: CardEnd;
	to: CardEnd;
	/** Where they change, in travel order ([] = direct). */
	via: string[];
	/** The shown path's change is at risk (`connection_at_risk` / `JourneyConnection.at_risk`). */
	viaAtRisk: boolean;
	/** The shortest change, minutes; null when not known. */
	changeMinutes: number | null;
	/** Other transit paths of the journey that are not the one drawn (people on one main flight can connect differently). */
	otherRoutes: number;
	status: FlightStatus;
	/** Times set by hand by the desk (tracking MANUAL). Callers drop it for a NOC. */
	manual: boolean;
}

// ── Times ───────────────────────────────────────────────────────────────────

/** Minutes from `a` to `b` (rounded); null when either is missing. */
export function minutesBetween(a: string | null, b: string | null): number | null {
	if (!a || !b) return null;
	const ms = Date.parse(b) - Date.parse(a);
	return Number.isFinite(ms) ? Math.round(ms / 60_000) : null;
}

/** The flight's length, end to end: positive minutes, else null. */
export function durationMinutes(m: Pick<FlightCardModel, "from" | "to">): number | null {
	const d = minutesBetween(m.from.at, m.to.at);
	return d !== null && d > 0 ? d : null;
}

/** `{ h, m }` of a duration, for "2h 05m". */
export const splitDuration = (minutes: number): { h: number; m: number } => ({ h: Math.floor(minutes / 60), m: minutes % 60 });

/**
 * Calendar days between take-off (its airport's day) and landing (its
 * airport's day): KUL 23:30 MYT → SGN 00:35 ICT = +1. 0 when either time is
 * missing.
 */
export function dayOffset(from: Pick<CardEnd, "at" | "zone">, to: Pick<CardEnd, "at" | "zone">): number {
	const a = wallTimeIn(from.at, from.zone.timeZone)?.date;
	const b = wallTimeIn(to.at, to.zone.timeZone)?.date;
	if (!a || !b) return 0;
	return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** An end's clock in its zone ("8:15AM", the GMS clock shape) and its calendar day there. */
export function endTime(at: string | null, zone: CardZone): { clock: string; date: string } | null {
	const w = wallTimeIn(at, zone.timeZone);
	return w ? { clock: formatClockIn(at, zone.timeZone), date: w.date } : null;
}

/** Zones people know by a name Intl does not print (the Games' own). */
const KNOWN_ZONES: Readonly<Record<string, string>> = { "Asia/Kuala_Lumpur": "MYT" };
const ZONE_LABEL = new Map<string, Intl.DateTimeFormat | null>();

/**
 * A zone's short label in the UI language ("GMT+7"; the Games' "MYT"),
 * through `intlLocaleFor` — never `bm` / `tet` handed to Intl.
 */
export function zoneLabel(timeZone: string, locale: string, at: Date = new Date()): string {
	const known = KNOWN_ZONES[timeZone];
	if (known) return known;
	const loc = intlLocaleFor(locale);
	const key = `${loc}|${timeZone}`;
	let f = ZONE_LABEL.get(key);
	if (f === undefined) {
		try {
			f = new Intl.DateTimeFormat(loc, { timeZone, timeZoneName: "shortOffset" });
		} catch {
			f = null;
		}
		ZONE_LABEL.set(key, f);
	}
	return f?.formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? timeZone;
}

/** Arrival pickup: expected landing + the entry point's dwell (the rule behind `JourneyDetail.pickup_at`). */
export function pickupAt(expectedLanding: string | null, dwellMinutes: number): string | null {
	if (!expectedLanding) return null;
	const t = Date.parse(expectedLanding);
	return Number.isFinite(t) ? new Date(t + dwellMinutes * 60_000).toISOString() : null;
}

/** Departure: at the airport by expected take-off − the entry point's departure lead. */
export function atAirportBy(expectedTakeOff: string | null, leadMinutes: number): string | null {
	if (!expectedTakeOff) return null;
	const t = Date.parse(expectedTakeOff);
	return Number.isFinite(t) ? new Date(t - leadMinutes * 60_000).toISOString() : null;
}

// ── Status ──────────────────────────────────────────────────────────────────

/** The card's status chip, every value (the approved set). */
export const CARD_STATUSES = ["on_time", "delayed", "departed", "landed", "cancelled", "diverted", "no_news", "not_tracked"] as const;
export type CardStatusKey = (typeof CARD_STATUSES)[number];
export type CardTone = "ok" | "warn" | "navy" | "bad" | "mute" | "line";

export const CARD_STATUS_TONE: Record<CardStatusKey, CardTone> = {
	on_time: "ok",
	delayed: "warn",
	departed: "navy",
	landed: "ok",
	cancelled: "bad",
	diverted: "bad",
	no_news: "mute",
	not_tracked: "line",
};

export interface CardStatus {
	key: CardStatusKey;
	tone: CardTone;
	/** `delayed`: minutes late at the Games end, when known and ≥ 1. */
	minutes: number | null;
	/** `landed`: when (the destination's expected = actual). */
	at: string | null;
}

/** The Games end of the main flight: where it lands (ARRIVAL) / leaves from (DEPARTURE). */
const gamesEnd = (m: Pick<FlightCardModel, "direction" | "from" | "to">): CardEnd => (m.direction === "ARRIVAL" ? m.to : m.from);

/**
 * The flight's status as the card says it: no flight → Not tracked; SCHEDULED
 * expected within FLIGHT_DELAY_THRESHOLD_MINUTES of schedule → On time;
 * DELAYED (or SCHEDULED but that late) → "Delayed N min" at the Games end;
 * DEPARTED / LANDED (with its time) / CANCELLED / DIVERTED; UNKNOWN → No news.
 */
export function cardStatus(m: Pick<FlightCardModel, "tracked" | "status" | "direction" | "from" | "to">): CardStatus {
	const of = (key: CardStatusKey, minutes: number | null = null, at: string | null = null): CardStatus => ({ key, tone: CARD_STATUS_TONE[key], minutes, at });
	if (!m.tracked) return of("not_tracked");
	switch (m.status) {
		case "DEPARTED":
			return of("departed");
		case "LANDED":
			return of("landed", null, m.to.at);
		case "CANCELLED":
			return of("cancelled");
		case "DIVERTED":
			return of("diverted");
		case "UNKNOWN":
			return of("no_news");
		default: {
			const end = gamesEnd(m);
			const late = minutesBetween(end.scheduled, end.at);
			if (m.status === "DELAYED" || (late !== null && late >= FLIGHT_DELAY_THRESHOLD_MINUTES)) return of("delayed", late !== null && late >= 1 ? late : null);
			return of("on_time");
		}
	}
}

/** The card gets the red edge: the flight is cancelled / diverted, or a connection is at risk. */
export const cardAlarm = (m: Pick<FlightCardModel, "status">, connectionAtRisk: boolean): boolean => m.status === "CANCELLED" || m.status === "DIVERTED" || connectionAtRisk;

// ── Stops ───────────────────────────────────────────────────────────────────

/** "Direct" / "1 stop · SIN" / "2 stops · SIN, BKK" — what the label is made of (the words are translated by the caller). */
export function stopLabel(via: readonly string[]): { stops: number; airports: string } {
	return { stops: via.length, airports: via.join(", ") };
}

// ── Builders ────────────────────────────────────────────────────────────────

/** The entry point's name when it is this airport (the Games end reads "KLIA · Terminal 1"). */
const entryPlace = (ep: Pick<EntryPointRef, "name" | "iata_code"> | null | undefined, code: string | null): string | null =>
	ep && code && ep.iata_code?.toUpperCase() === code.toUpperCase() ? ep.name : null;

function flightEnds(f: FlightSummary, direction: TravelDirection, ep: Pick<EntryPointRef, "name" | "iata_code"> | null | undefined): { from: CardEnd; to: CardEnd } {
	return {
		from: {
			code: f.origin_iata,
			place: (direction === "DEPARTURE" ? entryPlace(ep, f.origin_iata) : null) ?? f.origin_name,
			terminal: null,
			at: f.expected_departure_at ?? f.scheduled_departure_at,
			scheduled: f.scheduled_departure_at,
			zone: airportZone(f.origin_time_zone),
		},
		to: {
			code: f.destination_iata,
			place: (direction === "ARRIVAL" ? entryPlace(ep, f.destination_iata) : null) ?? f.destination_name,
			terminal: f.destination_terminal,
			at: f.expected_arrival_at ?? f.scheduled_arrival_at,
			scheduled: f.scheduled_arrival_at,
			zone: airportZone(f.destination_time_zone),
		},
	};
}

/** One found flight, alone (the leg form's lookup hit, a connection row). */
export function cardFromFlight(direction: TravelDirection, f: FlightSummary, entryPoint?: Pick<EntryPointRef, "name" | "iata_code"> | null): FlightCardModel {
	return {
		tracked: true,
		direction,
		mode: "AIR",
		carrier: f.carrier_code,
		name: formatFlightDesignator(f.carrier_code, f.flight_number),
		day: f.service_date,
		...flightEnds(f, direction, entryPoint),
		via: [],
		viaAtRisk: false,
		changeMinutes: null,
		otherRoutes: 0,
		status: f.status,
		manual: f.tracking === "MANUAL",
	};
}

/** A typed text field as shown: trimmed, blank = null. */
const typedText = (v: string | null): string | null => v?.trim() || null;

/** No flight on record: the entry point's end carries the typed time (Games' zone); the other end is the typed origin, no time. */
function typedCard(
	x: { direction: TravelDirection; mode: TravelMode; service_no: string | null; origin: string | null; entry_point: EntryPointRef; travel_date: string; scheduled_at: string | null; expected_at: string | null; status: FlightStatus },
	gamesTimeZone: string,
): FlightCardModel {
	const zone = typedZone(gamesTimeZone);
	const ours: CardEnd = { code: x.entry_point.iata_code ?? x.entry_point.code, place: x.entry_point.name, terminal: x.entry_point.terminal, at: x.expected_at ?? x.scheduled_at, scheduled: x.scheduled_at, zone };
	// Typed free text ("  kota kinabalu "): trimmed, blank = not given. Never upper-cased into a fake code — it is a place NAME.
	const other: CardEnd = { code: null, place: typedText(x.origin), terminal: null, at: null, scheduled: null, zone };
	const [from, to] = x.direction === "ARRIVAL" ? [other, ours] : [ours, other];
	return {
		tracked: false,
		direction: x.direction,
		mode: x.mode,
		carrier: null,
		name: typedText(x.service_no),
		day: x.travel_date,
		from,
		to,
		via: [],
		viaAtRisk: false,
		changeMinutes: null,
		otherRoutes: 0,
		status: x.status,
		manual: false,
	};
}

/**
 * Which of a journey's transit paths the card draws: the biggest path
 * (`connections` come most people first) when it carries at least as many
 * people as fly direct; else the main flight alone. `others` = the paths not
 * drawn (direct people count as one path; unlisted paths are counted, the
 * direct share is unknown then and not added).
 */
export function pickPath(j: Pick<Journey, "people" | "connections" | "connections_more">): { path: JourneyConnection | null; others: number } {
	const listed = j.connections.reduce((s, c) => s + c.people, 0);
	const direct = j.connections_more > 0 ? 0 : Math.max(0, j.people - listed);
	const routes = j.connections.length + j.connections_more + (direct > 0 ? 1 : 0);
	const top = j.connections[0] ?? null;
	const path = top && top.people >= direct ? top : null;
	return { path, others: Math.max(0, routes - 1) };
}

/** A journey of the A&D list / detail head. `gamesTimeZone` reads a typed time. */
export function cardFromJourney(
	j: Pick<
		Journey,
		| "direction"
		| "mode"
		| "flight"
		| "service_no"
		| "origin"
		| "entry_point"
		| "travel_date"
		| "scheduled_at"
		| "expected_at"
		| "status"
		| "people"
		| "connections"
		| "connections_more"
	>,
	gamesTimeZone: string,
): FlightCardModel {
	const f = j.flight;
	if (!f) return typedCard(j, gamesTimeZone);
	const base = cardFromFlight(j.direction, f, j.entry_point);
	const { path, others } = pickPath(j);
	if (!path) return { ...base, status: j.status, otherRoutes: others };
	const card: FlightCardModel = { ...base, status: j.status, via: path.via_iata, viaAtRisk: path.at_risk, changeMinutes: path.min_change_minutes, otherRoutes: others };
	if (j.direction === "ARRIVAL")
		card.from = { code: path.first_origin_iata, place: null, terminal: null, at: path.first_departure_at, scheduled: null, zone: airportZone(path.first_origin_time_zone) };
	else card.to = { code: path.last_destination_iata, place: null, terminal: null, at: path.last_arrival_at, scheduled: null, zone: airportZone(path.last_destination_time_zone) };
	return card;
}

/** One leg of a record (the record page's Travel step): its whole chain of flights, or the typed service. */
export function cardFromLeg(leg: Pick<TravelLeg, "direction" | "mode" | "flight" | "connections" | "connection_at_risk" | "service_no" | "origin" | "entry_point" | "travel_date" | "scheduled_at" | "expected_at">, gamesTimeZone: string): FlightCardModel {
	const f = leg.flight;
	// A typed leg has no flight status: the card says "Not tracked" whatever it is (a delay shows as the struck-through time).
	if (!f) return typedCard({ ...leg, status: "SCHEDULED" }, gamesTimeZone);
	const base = cardFromFlight(leg.direction, f, leg.entry_point);
	if (leg.connections.length === 0) return base;
	const chain = legFlightChain(leg.direction, f, leg.connections);
	const first = chain[0]!;
	const last = chain[chain.length - 1]!;
	const via: string[] = [];
	let change: number | null = null;
	for (let i = 0; i < chain.length - 1; i++) {
		const code = chain[i]!.destination_iata ?? chain[i + 1]!.origin_iata;
		if (code) via.push(code);
		const c = minutesBetween(chain[i]!.expected_arrival_at, chain[i + 1]!.expected_departure_at);
		if (c !== null && (change === null || c < change)) change = c;
	}
	return {
		...base,
		from: leg.direction === "ARRIVAL" ? flightEnds(first, leg.direction, leg.entry_point).from : base.from,
		to: leg.direction === "DEPARTURE" ? flightEnds(last, leg.direction, leg.entry_point).to : base.to,
		via,
		viaAtRisk: leg.connection_at_risk,
		changeMinutes: change,
	};
}
